import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { getData } from 'features/dinamic';
import { setDataToast, setVisibleToast } from 'features/toast';
import AlertInterface from 'features/toast/toastInterface';

type LoadStatus = 'idle' | 'loading' | 'completed' | 'failed';

interface FacilitadorDiplomaState {
  statusListado: LoadStatus;
  statusAutorizar: LoadStatus;
  error: string | null;
  diplomas: any[];
  authorizingId: number | null;
}

const initialState: FacilitadorDiplomaState = {
  statusListado: 'idle',
  statusAutorizar: 'idle',
  error: null,
  diplomas: [],
  authorizingId: null,
};

const parseData = (data: any) => {
  if (!data) return null;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch (_) {
      return data;
    }
  }
  return data;
};

export const fetchDiplomasPendientes = createAsyncThunk(
  'facilitadorDiplomas/fetchPendientes',
  async ({ idCiclo }: { idCiclo: number }, { dispatch, getState, rejectWithValue }) => {
    const { result, message, data } = await getData({ idsp: 149, idCiclo }, getState, dispatch);
    if (result) {
      const parsed = parseData(data);
      return Array.isArray(parsed) ? parsed : [];
    }
    return rejectWithValue(message || 'No fue posible obtener los diplomas');
  }
);

export const authorizeDiploma = createAsyncThunk(
  'facilitadorDiplomas/authorize',
  async (
    {
      idAlumnoDiplomadoResultado,
      observaciones,
      idCiclo,
    }: { idAlumnoDiplomadoResultado: number; observaciones?: string; idCiclo?: number },
    { dispatch, getState, rejectWithValue }
  ) => {
    const payload: any = {
      idsp: 150,
      idAlumnoDiplomadoResultado,
    };

    const trimmed = observaciones?.trim();
    if (trimmed) {
      payload.observaciones = trimmed;
    }

    const { result, message, data } = await getData(payload, getState, dispatch);
    let alert: AlertInterface;

    if (result) {
      alert = { title: 'CORRECTO..!', message: message || 'DIPLOMA AUTORIZADO', data: [], type: 'LimeGreen', time: Date.now() };
      if (idCiclo) {
        dispatch(fetchDiplomasPendientes({ idCiclo }));
      }
    } else {
      alert = { title: 'ACCIÓN INCOMPLETA', message: message || 'No fue posible autorizar el diploma', data, type: 'Crimson', time: Date.now() };
    }

    dispatch(setDataToast(alert));
    dispatch(setVisibleToast(true));
    setTimeout(() => {
      dispatch(setVisibleToast(false));
    }, 4000);

    if (result) {
      return { data };
    }
    return rejectWithValue(message || 'No fue posible autorizar el diploma');
  }
);

const facilitadorDiplomaSlice = createSlice({
  name: 'facilitadorDiplomas',
  initialState,
  reducers: {
    clearDiplomasPendientes: (state) => {
      state.statusListado = 'idle';
      state.statusAutorizar = 'idle';
      state.error = null;
      state.diplomas = [];
      state.authorizingId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDiplomasPendientes.pending, (state) => {
        state.statusListado = 'loading';
        state.error = null;
      })
      .addCase(fetchDiplomasPendientes.fulfilled, (state, action) => {
        state.statusListado = 'completed';
        state.diplomas = action.payload || [];
      })
      .addCase(fetchDiplomasPendientes.rejected, (state, action) => {
        state.statusListado = 'failed';
        state.error = (action.payload as string) || 'Error al cargar diplomas';
      })
      .addCase(authorizeDiploma.pending, (state, action) => {
        state.statusAutorizar = 'loading';
        state.authorizingId = action.meta.arg.idAlumnoDiplomadoResultado;
      })
      .addCase(authorizeDiploma.fulfilled, (state) => {
        state.statusAutorizar = 'completed';
        state.authorizingId = null;
      })
      .addCase(authorizeDiploma.rejected, (state, action) => {
        state.statusAutorizar = 'failed';
        state.authorizingId = null;
        state.error = (action.payload as string) || 'Error al autorizar diploma';
      });
  },
});

export const selectFacilitadorDiplomas = (state: RootState) => state.facilitadorDiplomas.diplomas;
export const selectFacilitadorDiplomasStatus = (state: RootState) => state.facilitadorDiplomas.statusListado;
export const selectFacilitadorDiplomasError = (state: RootState) => state.facilitadorDiplomas.error;
export const selectFacilitadorDiplomasAutorizarStatus = (state: RootState) => state.facilitadorDiplomas.statusAutorizar;
export const selectFacilitadorDiplomasAuthorizingId = (state: RootState) => state.facilitadorDiplomas.authorizingId;

export const { clearDiplomasPendientes } = facilitadorDiplomaSlice.actions;

export default facilitadorDiplomaSlice.reducer;
